import CoreBase from '../../services/engines/CoreBase';
import UmlCompositeShape from '../Types/UmlCompositeShape';

/**
 * Resolves UmlCompositeShape (package) containment: adopts top-level shapes that
 * fall within a package's bounds, and escapes children that fall outside them.
 * Extends CoreBase for logging only — holds no state of its own.
 */
class PackageContainmentHandler extends CoreBase {
    _name = 'PackageContainmentHandler';

    /**
     * Adopts a top-level, non-parented shape into an existing package if it falls within its bounds.
     * @param {Array} shapes - current top-level shapes array
     * @param {object} shape - shape to check for adoption
     * @returns {Array} updated shapes array (shape removed from it if adopted)
     */
    tryAdopt(shapes, shape) {
        if (shape.parentId) {
            return shapes;
        }
        const newParent = shapes.find((candidate) => candidate instanceof UmlCompositeShape
            && candidate !== shape
            && candidate.containsShape(shape));
        if (!newParent) {
            return shapes;
        }
        newParent.addChild(shape); // sets parentId + zIndex above the package
        this.logDebug('tryAdopt', `shape ${shape.id} adopted into package ${newParent.id}`);
        return shapes.filter((candidate) => candidate.id !== shape.id);
    }

    /**
     * Rechecks containment after a package resize: escapes children now outside the new
     * bounds, and adopts top-level, non-parented shapes that now fall within it.
     * @param {Array} shapes - current top-level shapes array
     * @param {object} packageShape - the resized package
     * @param {number} nextZIndex - current z-index counter
     * @returns {{shapes: Array, nextZIndex: number}} updated shapes array and z-index counter
     */
    recheckContainment(shapes, packageShape, nextZIndex) {
        let updatedShapes = shapes;
        let zIndex = nextZIndex;

        [...packageShape.children].forEach((child) => {
            if (!packageShape.containsShape(child)) {
                packageShape.removeChild(child.id);
                child.parentId = null;
                updatedShapes = [...updatedShapes, child];
                child.bringToFront(zIndex);
                zIndex += 1;
            }
        });

        const adoptedShapes = updatedShapes.filter((candidate) => candidate !== packageShape
            && !candidate.parentId
            && packageShape.containsShape(candidate));
        adoptedShapes.forEach((shape) => {
            updatedShapes = updatedShapes.filter((candidate) => candidate.id !== shape.id);
            packageShape.addChild(shape);
        });

        this.logDebug('recheckContainment', `package ${packageShape.id} | escaped/adopted recheck done`);
        return { shapes: updatedShapes, nextZIndex: zIndex };
    }
}

const packageContainmentHandler = new PackageContainmentHandler();
export default packageContainmentHandler;
